import * as React from 'react'
import { toast } from 'sonner'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useTranslations } from '@/hooks/use-translations'
import { cn } from '@/lib/utils'
import {
  useBusinessesQuery,
  useCatalogProductMutations,
  useCatalogProductsQuery,
  useCatalogsQuery,
  useCommentMediaQuery,
  useCreateCatalog,
} from '@/queries/meta'
import type { CatalogProductInput } from '@/services/instagramService'

const EMPTY_PRODUCT: CatalogProductInput = { name: '', description: '', price: '', currency: 'COP', image_url: '', url: '' }

/**
 * Product catalogs of the business behind the Page (`catalog_management`):
 * choose or create a catalog, add products -picking the photo from the Page's
 * own posts- and remove them. They appear in Meta Commerce Manager.
 */
export function CatalogTab() {
  const { t } = useTranslations()
  const businesses = useBusinessesQuery()
  const [businessId, setBusinessId] = React.useState('')
  const catalogs = useCatalogsQuery(businessId)
  const createCatalog = useCreateCatalog(businessId)
  const [catalogId, setCatalogId] = React.useState('')
  const [catalogName, setCatalogName] = React.useState('')
  const products = useCatalogProductsQuery(catalogId)
  const { create, remove } = useCatalogProductMutations(catalogId)
  const posts = useCommentMediaQuery('facebook')
  const [product, setProduct] = React.useState<CatalogProductInput>(EMPTY_PRODUCT)
  const [errors, setErrors] = React.useState<Partial<Record<keyof CatalogProductInput, string>>>({})

  React.useEffect(() => {
    if (!businessId && businesses.data?.length) setBusinessId(businesses.data[0].id)
  }, [businessId, businesses.data])

  React.useEffect(() => {
    if (!catalogId && catalogs.data?.length) setCatalogId(catalogs.data[0].id)
  }, [catalogId, catalogs.data])

  const set = (field: keyof CatalogProductInput) => (value: string) =>
    setProduct((current) => ({ ...current, [field]: value }))

  const submitCatalog = (event: React.FormEvent) => {
    event.preventDefault()
    if (!catalogName.trim()) return
    createCatalog.mutate(catalogName.trim(), {
      onSuccess: (created) => {
        setCatalogId(created.id)
        setCatalogName('')
        toast.success(t('facebook_page.catalog.catalog_created'))
      },
      onError: (err) => toast.error(`${t('facebook_page.catalog.error')}: ${err.message}`),
    })
  }

  const submitProduct = (event: React.FormEvent) => {
    event.preventDefault()
    const next = {
      name: product.name.trim() ? undefined : t('facebook_page.catalog.name_required'),
      price: Number(product.price.replace(',', '.')) >= 0 && product.price.trim() ? undefined : t('facebook_page.catalog.price_invalid'),
      image_url: product.image_url ? undefined : t('facebook_page.catalog.image_required'),
    }
    setErrors(next)
    if (next.name || next.price || next.image_url) return
    create.mutate(
      { ...product, price: product.price.replace(',', '.') },
      {
        onSuccess: () => {
          setProduct(EMPTY_PRODUCT)
          toast.success(t('facebook_page.catalog.product_created'))
        },
        onError: (err) => toast.error(`${t('facebook_page.catalog.error')}: ${err.message}`),
      }
    )
  }

  if (businesses.isLoading) return <Skeleton className="h-64 w-full" />
  if (businesses.error) {
    return <p className="text-sm text-destructive">{t('facebook_page.load_error')}: {businesses.error.message}</p>
  }

  return (
    <div className="space-y-8">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">{t('facebook_page.catalog.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('facebook_page.catalog.description')}</p>
      </div>

      <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="catalog-business">{t('facebook_page.catalog.business')}</Label>
          <Select value={businessId} onValueChange={(value) => { setBusinessId(value); setCatalogId('') }}>
            <SelectTrigger id="catalog-business"><SelectValue /></SelectTrigger>
            <SelectContent>
              {businesses.data?.map((business) => (
                <SelectItem key={business.id} value={business.id}>{business.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="catalog-select">{t('facebook_page.catalog.catalog')}</Label>
          <Select value={catalogId} onValueChange={setCatalogId}>
            <SelectTrigger id="catalog-select">
              <SelectValue placeholder={t('facebook_page.catalog.no_catalogs')} />
            </SelectTrigger>
            <SelectContent>
              {catalogs.data?.map((catalog) => (
                <SelectItem key={catalog.id} value={catalog.id}>{catalog.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <form onSubmit={submitCatalog} className="flex max-w-2xl items-end gap-2" noValidate>
        <div className="flex-1 space-y-1">
          <Label htmlFor="catalog-name">{t('facebook_page.catalog.new_catalog')}</Label>
          <Input
            id="catalog-name"
            aria-label={t('facebook_page.catalog.new_catalog')}
            value={catalogName}
            placeholder={t('facebook_page.catalog.new_catalog_placeholder')}
            onChange={(event) => setCatalogName(event.target.value)}
          />
        </div>
        <Button type="submit" variant="outline" disabled={!businessId || createCatalog.isPending}>
          {t('facebook_page.catalog.create_catalog')}
        </Button>
      </form>

      {catalogId && (
        <form onSubmit={submitProduct} className="max-w-2xl space-y-4" noValidate>
          <h2 className="text-lg font-semibold">{t('facebook_page.catalog.add_product')}</h2>

          <div className="space-y-1">
            <Label htmlFor="product-name">{t('facebook_page.catalog.name')}</Label>
            <Input
              id="product-name"
              aria-label={t('facebook_page.catalog.name')}
              value={product.name}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'product-name-error' : undefined}
              onChange={(event) => set('name')(event.target.value)}
            />
            <FieldError id="product-name-error">{errors.name}</FieldError>
          </div>

          <div className="space-y-1">
            <Label htmlFor="product-description">{t('facebook_page.catalog.product_description')}</Label>
            <Textarea
              id="product-description"
              aria-label={t('facebook_page.catalog.product_description')}
              rows={2}
              value={product.description}
              onChange={(event) => set('description')(event.target.value)}
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2 space-y-1">
              <Label htmlFor="product-price">{t('facebook_page.catalog.price')}</Label>
              <Input
                id="product-price"
                aria-label={t('facebook_page.catalog.price')}
                inputMode="decimal"
                value={product.price}
                aria-invalid={!!errors.price}
                aria-describedby={errors.price ? 'product-price-error' : undefined}
                onChange={(event) => set('price')(event.target.value)}
              />
              <FieldError id="product-price-error">{errors.price}</FieldError>
            </div>
            <div className="space-y-1">
              <Label htmlFor="product-currency">{t('facebook_page.catalog.currency')}</Label>
              <Input
                id="product-currency"
                aria-label={t('facebook_page.catalog.currency')}
                maxLength={3}
                value={product.currency}
                onChange={(event) => set('currency')(event.target.value.toUpperCase())}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('facebook_page.catalog.image')}</Label>
            <div className="flex flex-wrap gap-2">
              {posts.data?.filter((post) => post.media_url).map((post) => (
                <Button
                  key={post.id}
                  type="button"
                  variant="ghost"
                  size="thumb"
                  className={cn(product.image_url === post.media_url && 'ring-2 ring-primary')}
                  aria-label={t('facebook_page.catalog.use_image')}
                  onClick={() => set('image_url')(post.media_url ?? '')}
                >
                  <img src={post.media_url} alt="" className="h-full w-full rounded-md object-cover" />
                </Button>
              ))}
            </div>
            <Input
              id="product-image"
              aria-label={t('facebook_page.catalog.image_url')}
              value={product.image_url}
              placeholder={t('facebook_page.catalog.image_url')}
              aria-invalid={!!errors.image_url}
              aria-describedby={errors.image_url ? 'product-image-error' : undefined}
              onChange={(event) => set('image_url')(event.target.value)}
            />
            <FieldError id="product-image-error">{errors.image_url}</FieldError>
          </div>

          <div className="space-y-1">
            <Label htmlFor="product-url">{t('facebook_page.catalog.link')}</Label>
            <Input
              id="product-url"
              aria-label={t('facebook_page.catalog.link')}
              value={product.url}
              placeholder={t('facebook_page.catalog.link_placeholder')}
              onChange={(event) => set('url')(event.target.value)}
            />
          </div>

          <Button type="submit" disabled={create.isPending}>{t('facebook_page.catalog.add')}</Button>
        </form>
      )}

      {catalogId && (
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">{t('facebook_page.catalog.products')}</h2>
          {products.isLoading && <Skeleton className="h-24 w-full" />}
          {products.error && (
            <p className="text-sm text-destructive">{t('facebook_page.load_error')}: {products.error.message}</p>
          )}
          {products.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('facebook_page.catalog.empty')}</p>
          )}
          <ul className="space-y-2">
            {products.data?.map((item) => (
              <li key={item.id} className="flex items-center gap-4 rounded-lg border p-4">
                {item.image_url && <img src={item.image_url} alt="" className="h-14 w-14 rounded-md object-cover" />}
                <div className="flex-1 space-y-1">
                  <p className="text-sm font-semibold">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.price}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('facebook_page.catalog.delete')}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(item.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
